/* eslint-disable camelcase */
import _ from 'lodash';
import Service from './abstract';
import { types } from '../constants/files/types';
import platforms from '../constants/users/platforms';
import { DOCUMENT_TYPES_FILTER } from '../constants/users/createUser';
const BUCKET = process.env.REACT_APP_PUBLIC_S3_BUCKET || 'statictemporalnatura';

const withPersonSmall = 'person,person.attributes,person.attributes.attribute';
const withPersonEmail = 'person.emails,person.emails.type';
const withPersonDocuments = 'person.documents,person.documents.type';
const withPersonTelephones = 'person.telephones,person.telephones.type';
const withPersonAddress = 'person.addresses,person.addresses.state,person.addresses.country,person.addresses.city,person.addresses.type';
const withPersonLevels = 'person.levels,person.levels.type';
const withDevices = 'person.devices,person.devices.type';
const withPerson = `${withPersonSmall},${withDevices},${withPersonTelephones},${withPersonDocuments},${withPersonEmail},${withPersonLevels},`;

const defaultWith = `roles,roles.role,status,${withPerson}`;
const fullWith = `${defaultWith},${withPersonAddress}`;
const fullWidthSmall = `${withPersonSmall},${withPersonDocuments}`;

const defaultOptionsFind = {
    order: 'created_at-DESC',
    with: defaultWith
};
const defaultOptionsGetById = {
    with: fullWith
};

const defaultOptionsFindSmall = {
    order: 'created_at-DESC',
    with: fullWidthSmall
};

const defaultEvents = {
    tiny: false
};

class Users extends Service {
    constructor () {
        super('users');
    }

    get personWithSmall () {
        return withPersonSmall;
    }

    get personWith () {
        return withPerson;
    }

    get fullWith () {
        return fullWith;
    }

    get defaultOptionsFind () {
        return _.merge({}, super.defaultOptionsFind, defaultOptionsFind);
    }

    get defaultOptionsGetById () {
        return _.merge({}, super.defaultOptionsGetById, defaultOptionsGetById);
    }

    findSmall (data = {}) {
        const opts = {
            url: `${this.basePath}/users`,
            qs: _.defaultsDeep({}, data, defaultOptionsFindSmall),
        };

        return this.get(opts).then(users => this.normalizeAll(users));
    }

    findSimple (data = {}) {
        const opts = {
            url: `${this.basePath}/users`,
            qs: _.defaultsDeep({}, data, ''),
        };

        return this.get(opts).then(users => this.normalizeAll(users));
    }

    getAllSegments () {
        return this.get(`${this.basePath}/people/segments`);
    }

    getAllStatuses () {
        return this.get(`${this.basePath}/users/statuses`);
    }

    getDocumentTypes () {
        return this.get({
            url: `${this.basePath}/document/types`,
            qs: { filters: DOCUMENT_TYPES_FILTER }
        });
    }

    getRoles () {
        return this.get({
            url: `${this.basePath}/permissions/roles`,
            qs: { filters: `id_platform eq ${platforms.backoffice}` }
        });
    }

    toggle (id, data = {}) {
        const opts = {
            // TODO fix base url. When cross domain resolve for PUT method
            url: `${this.basePath}/users/${id}/toggle`,
            data
        };

        return this.put(opts).then(user => user);
    }

    editById (id, data = {}) {
        const opts = {
            // TODO fix base url. When cross domain resolve for PUT method
            url: `${this.basePath}/users/${id}`,
            data
        };

        return this.put(opts).then(users => this.normalize(users));
    }

    editRole (id, data = {}) {
        const opts = {
            url: `${this.basePath}/users/${id}/roles`,
            data: data.toCreate
        };

        return this.delete({
            url: `${this.basePath}/users/${id}/roles/${data.toDelete}`
        }).then(res => {
            return this.post(opts);
        });
    }

    blackList (id, data = {}) {
        const opts = {
            url: `${this.basePath}/users/${id}/blacklist`,
            data
        };

        return super.put(opts).then(user => this.normalize(user));
    }

    onboardingOperation (id) {
        const opts = {
            url: `${this.basePath}/users/${id}/vu/operation`
        };

        return super.get(opts);
    }

    userEvents (id, data) {
        const opts = {
            url: `${this.basePath}/users/${id}/events`,
            qs: _.defaultsDeep({}, data, defaultEvents)
        };

        return super.get(opts);
    }

    getFiles (options) {
        return this.get({
            url: `${this.basePath}/files`,
            qs: _.defaultsDeep({}, options, defaultOptionsFind)
        });
    }

    requestUploadFileUrl (options) {
        return this.get({
            url: `${this.basePath}/files/presigned`,
            qs: _.defaultsDeep({}, options)
        });
    }

    uploadFile (url, data) {
        // S3 upload file, request header authorization must be with one space. If you delete the header, it won't work, also if you put it as null
        this.r.defaults.headers.Authorization = ' ';
        return this.post({
            url,
            data,
        });
    }

    async uploadS3 (data) {
        const fileBlob = data.fileUri;
        const contentType = data.contentType || fileBlob?.type || 'text/csv';

        const params = new URLSearchParams({
            idType: data.idType ? data.idType : types.document.toString(),
            bucket: BUCKET,
            dirname: data.dirname,
            filename: data.filename,
            contentType: contentType
        });

        const { url, fields, id_file } = await this.get(
            `${this.basePath}/files/presigned?${params.toString()}`
        );
        const idFile = id_file;
        const signature = fields['X-Amz-Signature'];

        await this.put({
            url: `${this.basePath}/files/presigned/${idFile}`,
            data: {
                signature: signature
            }
        });

        const formData = new FormData();

        Object.keys(fields).forEach((key) => {
            formData.append(key, fields[key]);
        });

        formData.append('file', fileBlob);

        await fetch(url, {
            method: 'POST',
            body: formData
        });

        return { url: `${url}/${data.dirname}/${data.filename}`, idFile };
    }

    async uploadS3TaxpagerRegister (data) {
        const fileBlob = data.fileUri;
        const contentType = data.contentType || fileBlob?.type || 'text/csv';

        const params = new URLSearchParams({
            idType: data.idType ? data.idType : types.document.toString(),
            bucket: BUCKET,
            dirname: data.dirname,
            filename: data.filename,
            idUser: data.idUser,
            contentType: contentType
        });

        const { url, fields, id_file } = await this.get(
            `${this.basePath}/files/presigned/taxpager/census?${params.toString()}`
        );
        const idFile = id_file;
        const signature = fields['X-Amz-Signature'];

        await this.put({
            url: `${this.basePath}/files/presigned/taxpager/census/${idFile}`,
            data: {
                signature: signature,
                idUser: data.idUser,
                idType: data.idType
            }
        });

        const formData = new FormData();

        Object.keys(fields).forEach((key) => {
            formData.append(key, fields[key]);
        });

        formData.append('file', fileBlob);

        await fetch(url, {
            method: 'POST',
            body: formData
        });

        return { url: `${url}/${data.dirname}/${data.filename}`, idFile };
    }

    validateFiles (data) {
        const opts = {
            url: `${this.basePath}/people/prospect/validate`,
            data
        };

        return this.post(opts);
    }

    processFile (data, qs) {
        const opts = {
            url: `${this.basePath}/people/prospect/process`,
            data,
            qs: _.defaultsDeep({}, qs),

        };

        return this.post(opts);
    }

    updateReport (id, data) {
        return this.put({
            url: `${this.basePath}/files/presigned/${id}`,
            data: {
                signature: data['X-Amz-Signature'],
            }
        });
    }

    getUsersCreditCards (options) {
        return this.get({
            url: `${this.basePath}/creditcards/users/views`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getUserCollections (options) {
        return this.get({
            url: `${this.basePath}/creditcards/payments`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getUserWalletHome (id) {
        return this.get({
            url: `${this.basePath}/wallet/user/${id}/home`
        });
    }

    getMovementsCreditCards (options) {
        return this.get({
            url: `${this.basePath}/creditcards/news`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getUserWalletSummary (id) {
        return this.get({
            url: `${this.basePath}/wallet/user/summary`,
            qs: {
                idUser: id
            }
        });
    }

    getUserEmails (id) {
        return this.get({
            url: `${this.basePath}/emails/${id}`
        });
    }

    getUserTelephones (id) {
        return this.get({
            url: `${this.basePath}/telephones/${id}`
        }).catch(() => {
            return [];
        });
    }

    getSummary (options) {
        return this.get({
            url: `${this.basePath}/creditcards/statements`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getSummaries (options) {
        return this.get({
            url: `${this.basePath}/creditcards/statements/view`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getExpirations (options) {
        return this.get({
            url: `${this.basePath}/creditcards/metrics`,
            qs: _.defaultsDeep({}, options)
        });
    }

    normalize (user) {
        if (_.isNil(user)) {
            return user;
        }

        // Prepare default properties
        user = super.normalize(user);

        // Prepare person
        if (user.person) {
            // Prepare attributes
            if (user.person.attributes) {
                user.person.rawAttributes = user.person.attributes;
                user.person.attributes = _.keyBy(user.person.attributes, 'description');

                // Prepare formatted name.
                user.formattedName = '';
                if (user.person.attributes.first_name) {
                    user.formattedName = user.person.attributes.first_name.value;
                }
                if (user.person.attributes.last_name) {
                    user.formattedName = _.trim(`${user.formattedName} ${user.person.attributes.last_name.value}`);
                }
            }

            // Prepare documents
            if (!user.person.document && user.person.documents && user.person.documents.length) {
                user.person.document = _.find(user.person.documents, ['type.description', 'dni']) || user.person.documents[0];
                user.person.cuil = _.find(user.person.documents, ['type.description', 'cuil']) || '-';
                user.person.cuit = _.find(user.person.documents, ['type.description', 'cuit']) || '-';
            }

            // Prepare telephones
            if (!user.person.telephone && user.person.telephones && user.person.telephones.length) {
                user.person.telephone = _.find(user.person.telephones, ['type.description', 'mobile']) || user.person.telephones[0];
            }

            // Prepare emails
            if (!user.person.email && user.person.emails && user.person.emails.length) {
                user.person.email = _.find(user.person.emails, ['type.description', 'home']) || user.person.emails[0];
            }
        }

        return user;
    }

    getPaymentLinks (options) {
        return this.get({
            url: `${this.basePath}/wallet/links/payment/links`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getPaymentMovements (options) {
        return this.get({
            url: `${this.basePath}/wallet/links/payment-movements`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getPaymentLinksPosition (id, options) {
        return this.get({
            url: `${this.basePath}/paymentlinks/calculate/user/${id}`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getLevels () {
        return this.get(`${this.basePath}/people/levels`);
    }

    getConsultants (options) {
        return this.get({
            url: `${this.basePath}/people/prospect`,
            qs: _.defaultsDeep({}, options)
        });
    }

    executeCensus (options = {}) {
        const opts = {
            url: `${this.basePath}/afip/census/execute`,
            qs: _.defaultsDeep({}, options)
        };

        return this.get(opts);
    }
}

export default new Users();
